import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShoppingBag, MessageCircle, Plus, Minus, Trash2, ArrowLeft, X } from "lucide-react";
import api, { imgUrl } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

// ── Cart (localStorage) ──
const CART_KEY = "pz_cart";
function loadCart() { try { return JSON.parse(localStorage.getItem(CART_KEY) || "[]"); } catch { return []; } }
function saveCart(items) { localStorage.setItem(CART_KEY, JSON.stringify(items)); window.dispatchEvent(new Event("pz-cart")); }
export function addToCart(p, qty = 1) {
  const cart = loadCart();
  const idx = cart.findIndex((c) => c.id === p.id);
  if (idx >= 0) cart[idx].qty += qty;
  else cart.push({ id: p.id, name: p.name, price: p.price, image_url: p.image_url, qty });
  saveCart(cart);
}
export function useCart() {
  const [items, setItems] = useState(loadCart());
  useEffect(() => {
    const reload = () => setItems(loadCart());
    window.addEventListener("pz-cart", reload);
    window.addEventListener("storage", reload);
    return () => { window.removeEventListener("pz-cart", reload); window.removeEventListener("storage", reload); };
  }, []);
  return {
    items,
    setQty: (id, qty) => { const c = loadCart().map((x) => x.id === id ? { ...x, qty: Math.max(1, qty) } : x); saveCart(c); },
    remove: (id) => saveCart(loadCart().filter((x) => x.id !== id)),
    clear: () => saveCart([]),
  };
}

export function CartDrawer({ open, onClose }) {
  const { items, setQty, remove, clear } = useCart();
  const { settings } = useSettings();
  const total = useMemo(() => items.reduce((s, i) => s + i.qty * Number(i.price), 0), [items]);

  const checkoutOnWhatsApp = () => {
    if (items.length === 0) return;
    const lines = items.map((i) => `• ${i.name} × ${i.qty} — LKR ${(i.qty * Number(i.price)).toLocaleString()}`).join("\n");
    const msg = `Hi! I'd like to order:\n\n${lines}\n\nTotal: LKR ${total.toLocaleString()}\n\nPlease confirm availability & next steps.`;
    const wa = `https://wa.me/${(settings.whatsapp_number || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(msg)}`;
    window.open(wa, "_blank");
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[var(--pz-ink-2)] border-l border-[var(--pz-line)] flex flex-col">
        <div className="p-5 flex items-center justify-between border-b border-[var(--pz-line)]">
          <div className="font-display text-2xl uppercase font-bold tracking-wide text-white">Your Cart</div>
          <button onClick={onClose} data-testid="cart-close" className="h-9 w-9 grid place-items-center rounded-full border border-[var(--pz-line)] text-white hover:border-[var(--pz-red)]"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-4">
          {items.length === 0 && <div className="text-zinc-500 text-sm">Your cart is empty.</div>}
          {items.map((i) => (
            <div key={i.id} className="flex gap-3 items-center bg-[var(--pz-ink)] rounded-md p-3 border border-[var(--pz-line)]">
              {i.image_url && <img src={imgUrl(i.image_url)} alt={i.name} className="h-16 w-16 object-cover rounded-sm" />}
              <div className="flex-1">
                <div className="font-bold text-white leading-tight">{i.name}</div>
                <div className="text-sm text-zinc-400">LKR {Number(i.price).toLocaleString()}</div>
                <div className="flex items-center gap-2 mt-2">
                  <button data-testid={`cart-dec-${i.id}`} onClick={() => setQty(i.id, i.qty - 1)} className="h-7 w-7 grid place-items-center rounded-full bg-[var(--pz-ink-2)] border border-[var(--pz-line)] text-white"><Minus className="h-3 w-3" /></button>
                  <span className="font-bold w-6 text-center text-white">{i.qty}</span>
                  <button data-testid={`cart-inc-${i.id}`} onClick={() => setQty(i.id, i.qty + 1)} className="h-7 w-7 grid place-items-center rounded-full bg-[var(--pz-ink-2)] border border-[var(--pz-line)] text-white"><Plus className="h-3 w-3" /></button>
                </div>
              </div>
              <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)} className="h-8 w-8 grid place-items-center rounded-full text-[var(--pz-red)] hover:bg-[var(--pz-red)]/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="p-5 border-t border-[var(--pz-line)] space-y-3">
          <div className="flex items-center justify-between font-bold text-lg text-white">
            <span className="uppercase tracking-wider text-sm text-zinc-400">Total</span>
            <span>LKR {total.toLocaleString()}</span>
          </div>
          <button onClick={checkoutOnWhatsApp} disabled={items.length === 0} data-testid="cart-checkout-whatsapp" className="btn-red w-full justify-center disabled:opacity-50"><MessageCircle className="h-4 w-4" /> Checkout on WhatsApp</button>
          {items.length > 0 && <button onClick={clear} data-testid="cart-clear" className="text-sm text-zinc-500 hover:text-[var(--pz-red)] uppercase tracking-[0.15em]">Clear cart</button>}
        </div>
      </aside>
    </div>
  );
}

export function ShopList() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [cartOpen, setCartOpen] = useState(false);
  const { items } = useCart();

  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data)); }, []);

  const cats = ["all", ...Array.from(new Set(products.map((p) => p.category)))];
  const filtered = filter === "all" ? products : products.filter((p) => p.category === filter);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <div data-testid="shop-page" className="bg-[var(--pz-ink)] text-white">
      <SEOHead title="Shop — The Arena" description="Jerseys, gear, tournament entries and private bookings. Checkout via WhatsApp." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.35em] text-[var(--pz-red)] font-bold mb-4">Club Store</div>
            <h1 className="font-display text-5xl md:text-7xl font-bold uppercase leading-none">Shop</h1>
            <p className="mt-6 text-zinc-400 max-w-2xl">Jerseys, gear and tournament slots. Checkout instantly via WhatsApp — we'll confirm stock and delivery in one chat.</p>
          </div>
          <button data-testid="open-cart-btn" onClick={() => setCartOpen(true)} className="btn-outline relative">
            <ShoppingBag className="h-4 w-4" /> Cart
            {count > 0 && <span className="absolute -top-1 -right-1 bg-[var(--pz-red)] text-white text-[10px] h-5 min-w-[20px] px-1 rounded-full grid place-items-center font-bold">{count}</span>}
          </button>
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`shop-filter-${c}`}
              onClick={() => setFilter(c)}
              className={`px-5 py-2 rounded-full text-xs uppercase tracking-[0.15em] font-bold capitalize transition ${filter === c ? "bg-[var(--pz-red)] text-white" : "border border-[var(--pz-line)] text-zinc-300 hover:border-[var(--pz-red)]"}`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {filtered.map((p, i) => (
            <div
              key={p.id}
              data-testid={`product-card-${i}`}
              className="group bg-[var(--pz-ink-2)] border border-[var(--pz-line)] rounded-md overflow-hidden hover:-translate-y-1 transition-all pz-fade-up flex flex-col"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <Link to={`/shop/${p.slug}`} className="overflow-hidden">
                {p.image_url && <img src={imgUrl(p.image_url)} alt={p.name} className="w-full aspect-square object-cover transition duration-700 group-hover:scale-105" />}
              </Link>
              <div className="p-5 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{p.category}</div>
                <Link to={`/shop/${p.slug}`} className="font-display text-lg font-bold uppercase mt-1 text-white group-hover:text-[var(--pz-red)] transition">{p.name}</Link>
                <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{p.description}</p>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <div className="font-bold text-white">LKR {Number(p.price).toLocaleString()}</div>
                  <button data-testid={`add-to-cart-${i}`} onClick={() => { addToCart(p); toast.success(`Added ${p.name}`); }} className="btn-red !px-5 !py-2.5 text-[10px]">Add</button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="col-span-full text-zinc-500">No products in this category.</div>}
        </div>
      </div>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}

export function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const { settings } = useSettings();

  useEffect(() => { api.get(`/products/${slug}`).then(({ data }) => setP(data)).catch(() => setP(false)); }, [slug]);

  if (p === null) return <div className="bg-[var(--pz-ink)] py-32 text-center text-zinc-500">Loading…</div>;
  if (p === false) return <div className="bg-[var(--pz-ink)] py-32 text-center text-zinc-500">Product not found.</div>;

  const wa = `https://wa.me/${(settings.whatsapp_number || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi! I'd like to buy "${p.name}" (LKR ${Number(p.price).toLocaleString()}). Is it available?`)}`;

  return (
    <div data-testid="product-detail-page" className="bg-[var(--pz-ink)] text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SEOHead title={`${p.name} — The Arena`} description={p.description} />
        <Link to="/shop" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-xs uppercase tracking-[0.2em] font-bold mb-8"><ArrowLeft className="h-3.5 w-3.5" /> Back to shop</Link>
        <div className="grid md:grid-cols-2 gap-12">
          {p.image_url && <img src={imgUrl(p.image_url)} alt={p.name} className="w-full aspect-square object-cover rounded-md" />}
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--pz-red)] font-bold">{p.category}</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold uppercase mt-3 leading-none">{p.name}</h1>
            <div className="mt-5 text-3xl font-bold">LKR {Number(p.price).toLocaleString()}</div>
            <p className="mt-6 text-zinc-400 leading-relaxed">{p.description}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button data-testid="product-add-cart" onClick={() => { addToCart(p); toast.success("Added to cart"); setCartOpen(true); }} className="btn-red"><ShoppingBag className="h-4 w-4" /> Add to Cart</button>
              <a href={wa} target="_blank" rel="noreferrer" data-testid="product-buy-whatsapp" className="btn-outline"><MessageCircle className="h-4 w-4" /> Buy on WhatsApp</a>
            </div>
          </div>
        </div>
      </div>
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}
